import Paths from '@/configs/router/paths';

/**
 * Check if error is a chunk/module load error
 */
function isChunkLoadError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('ChunkLoadError') ||
    errorMessage.includes('Loading CSS chunk') ||
    errorMessage.includes('Failed to fetch')
  );
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return (
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('Network request failed')
  );
}

/**
 * Navigate to error page or open in new window
 */
function handleChunkLoadError(error: Error | string) {
  const errorMessage = typeof error === 'string' ? error : error.message;

  // Check if we're already on the error page to avoid infinite loops
  if (window.location.pathname === Paths.ERROR) {
    console.error('Already on error page, error:', errorMessage);
    return;
  }

  // Try to navigate using router if available
  try {
    // Check if router is available (will be set after router initialization)
    const router = (window as any).__router;
    if (router) {
      router.navigate(Paths.ERROR, {
        state: { error: errorMessage },
        replace: true,
      });
      return;
    }
  } catch (e) {
    console.warn('Router navigation failed, using window.location:', e);
  }

  // Fallback: use window.location or open new window
  // Store error in sessionStorage for error page to read
  try {
    sessionStorage.setItem('chunkLoadError', errorMessage);
    sessionStorage.setItem('errorTimestamp', Date.now().toString());
  } catch (e) {
    console.warn('Failed to store error in sessionStorage:', e);
  }

  // Navigate to error page
  window.location.href = Paths.ERROR;
}

/**
 * Get error code from error object
 */
export function getErrorCode(error: any): number | undefined {
  if (!error) return undefined;
  let code = error.code;
  try {
    const messageObj = JSON.parse(error.message || '{}');
    const errorObj = JSON.parse(messageObj?.error || '{}');
    if (errorObj?.code) {
      code = errorObj.code;
    }
  } catch (_) {
    // ignore
  }
  return code;
}

/**
 * Initialize global error handlers
 */
export function initErrorHandlers() {
  // Handle unhandled promise rejections (including dynamic import failures)
  // window.addEventListener('unhandledrejection', (event) => {
  //   const error = event.reason;
  //   const errorMessage = error?.message || error?.toString() || 'Unknown error';

  //   console.error('Unhandled promise rejection:', error);

  //   // Check if it's a chunk load error
  //   if (isChunkLoadError(errorMessage)) {
  //     event.preventDefault(); // Prevent default error logging
  //     handleChunkLoadError(errorMessage);
  //   }
  // });

  // Handle general errors
  window.addEventListener('error', (event) => {
    const error = event.error || event.message;
    const errorMessage = error?.message || error?.toString() || event.message || 'Unknown error';

    // Check if it's a chunk load error
    if (isChunkLoadError(errorMessage)) {
      event.preventDefault(); // Prevent default error logging
      handleChunkLoadError(errorMessage);
    }
  });

  // Handle React error boundaries (if needed)
  // This will be handled by the ErrorPage component via useRouteError
}

/**
 * Store router instance for error handling
 */
export function setRouter(router: any) {
  (window as any).__router = router;
}
